import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LottieView from 'lottie-react-native';
import Feather from '@react-native-vector-icons/feather/static';

import { useAppSelector } from '../../../redux/helper';
import {
  markWizardHomeLandingCelebrationShown,
  selectOwnerActivation,
  selectShouldPlayWizardHomeCelebration,
} from '../../../redux/ownerActivationSlice';
import {
  selectBusinessName,
  selectEmployeeName,
} from '../../../redux/AuthSlice';
import { isEmployeeOwnerRole } from '../../../utils/roles';
import ownerActivationAnalytics from '../../../services/ownerActivationAnalytics';
import type {
  OwnerActivationAnalyticsPlatform,
  WizardHomeCelebrationCtaId,
  WizardHomeCelebrationShownProperties,
} from '../../../services/ownerActivationAnalyticsTypes';
import { GTFonts, useTheme } from '../../../theme';
import { fs, s, v } from '../../../utils/libs';
import type { RootStackParamList } from '../../../navigation/AppStack';

/**
 * Post-wizard Home landing celebration.
 *
 * Shows a full-screen celebratory modal (headline + subline + CTA) with
 * background confetti the first time the owner reaches Home after finishing
 * OR skipping the setup wizard. The pending flag is persisted, so a mid-flow
 * app kill still delivers the moment on next Home landing. The flag is
 * cleared as soon as the modal mounts, guaranteeing single play.
 *
 * Rationale: the previous confetti was gated on all-five activation tasks
 * completing — a milestone most owners never reach in the first session, so
 * the celebratory moment was invisible. Tying it to wizard exit gives every
 * new owner the same "you did it" peak-end moment.
 */

const analyticsPlatform = (): OwnerActivationAnalyticsPlatform => {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return 'web';
};

type Nav = NativeStackNavigationProp<RootStackParamList>;

const CONFETTI_DURATION_MS = 7000;

const WizardHomeCelebrationController: React.FC = () => {
  const dispatch = useDispatch();
  const navigation = useNavigation<Nav>();
  const { theme } = useTheme();
  const { width, height } = useWindowDimensions();

  const shouldPlay = useAppSelector(selectShouldPlayWizardHomeCelebration);
  const owner = useAppSelector(selectOwnerActivation);
  const ownerName = useAppSelector(selectEmployeeName);
  const businessName = useAppSelector(selectBusinessName);
  const businessType = useAppSelector(
    state => state.auth.inApp.account.businessInfo?.type,
  );
  const employeeRole = useAppSelector(
    state => state.auth.inApp.account.employeeInfo?.bp_employee_role,
  );
  const isOwner = isEmployeeOwnerRole(employeeRole);

  const [visible, setVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  // Gate the show effect until the async reduce-motion query resolves,
  // otherwise RM users get a one-frame confetti flash before the initial
  // `false` default flips to `true`.
  const [reduceMotionResolved, setReduceMotionResolved] = useState(false);
  const shownRef = useRef(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled()
      .then(value => {
        if (!mounted) return;
        setReduceMotion(!!value);
        setReduceMotionResolved(true);
      })
      .catch(() => {
        if (!mounted) return;
        setReduceMotion(false);
        setReduceMotionResolved(true);
      });
    const sub = AccessibilityInfo.addEventListener?.(
      'reduceMotionChanged',
      (value: boolean) => setReduceMotion(!!value),
    );
    return () => {
      mounted = false;
      (sub as { remove?: () => void } | undefined)?.remove?.();
    };
  }, []);

  const wizardExitReason: 'completed' | 'skipped' | null =
    owner.wizard.status === 'completed'
      ? 'completed'
      : owner.wizard.status === 'skipped'
        ? 'skipped'
        : null;

  const trackerProps = useCallback((): WizardHomeCelebrationShownProperties => {
    return {
      onboarding_version: 1,
      platform: analyticsPlatform(),
      business_type_group: businessType || undefined,
      // Exit reason is guaranteed non-null when this fires (see mount guard).
      wizard_exit_reason: wizardExitReason ?? 'completed',
      reduced_motion: reduceMotion,
      product_created_during_wizard: owner.wizard.productCreatedDuringWizard,
    };
  }, [
    businessType,
    owner.wizard.productCreatedDuringWizard,
    reduceMotion,
    wizardExitReason,
  ]);

  // Mount guard: fire once per pending flag, only for owner role, only when
  // wizard actually exited (defensive — flag should imply this), and only
  // after the async reduce-motion query resolves so RM users never see a
  // confetti flash on first paint.
  useEffect(() => {
    if (!shouldPlay) return;
    if (!isOwner) return;
    if (!wizardExitReason) return;
    if (!reduceMotionResolved) return;
    if (shownRef.current) return;
    shownRef.current = true;

    // Clear the flag first so any remount (fast refresh, tab switch, second
    // Home mount from navigation.reset) can never replay the moment.
    dispatch(markWizardHomeLandingCelebrationShown());
    setVisible(true);

    try {
      ownerActivationAnalytics.track(
        'wizard_home_celebration_shown',
        trackerProps(),
      );
    } catch {
      // analytics must never break Home
    }
  }, [
    dispatch,
    isOwner,
    reduceMotionResolved,
    shouldPlay,
    trackerProps,
    wizardExitReason,
  ]);

  const dismiss = useCallback(
    (ctaId: WizardHomeCelebrationCtaId) => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      setVisible(false);
      try {
        ownerActivationAnalytics.track('wizard_home_celebration_dismissed', {
          ...trackerProps(),
          cta_id: ctaId,
        });
      } catch {
        // ignore
      }
    },
    [trackerProps],
  );

  const goPrimary = useCallback(() => {
    const productReady = owner.wizard.productCreatedDuringWizard;
    dismiss(
      productReady ? 'primary_start_selling' : 'primary_add_product',
    );
    // Defer navigation one frame so the modal close animation isn't
    // interrupted by a stack push on slower Android devices.
    requestAnimationFrame(() => {
      if (productReady) {
        navigation.navigate('MainScreen', {
          screen: 'ProductsScreen',
          params: { from: 'HomeScreen' },
        } as any);
      } else {
        navigation.navigate('NewProduct' as any);
      }
    });
  }, [dismiss, navigation, owner.wizard.productCreatedDuringWizard]);

  const goSecondary = useCallback(() => {
    dismiss('secondary_explore_later');
  }, [dismiss]);

  const closeX = useCallback(() => {
    dismiss('dismiss');
  }, [dismiss]);

  if (!visible) return null;

  const productReady = owner.wizard.productCreatedDuringWizard;
  const primaryLabel = productReady
    ? 'Start selling'
    : 'Add your first product';
  const microLine = productReady
    ? 'Your first sale is one tap away.'
    : 'Add a product to make your first sale.';

  const firstName = (ownerName || '').trim().split(/\s+/)[0] || '';
  const headline = firstName
    ? `You're all set, ${firstName}!`
    : "You're all set!";
  const subline = businessName
    ? `${businessName} is ready to run on BizPlus POS.`
    : 'Your workspace is ready to run on BizPlus POS.';

  const colors = theme.colors;

  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      onRequestClose={closeX}
      statusBarTranslucent
      accessibilityViewIsModal
      testID="wizard-home-celebration-modal">
      <View style={styles.root}>
        {/* Backdrop */}
        <View
          style={[styles.backdrop, { backgroundColor: 'rgba(0,0,0,0.45)' }]}
        />

        {/* Confetti — behind the card, full screen, non-interactive. */}
        {!reduceMotion && (
          <View style={styles.confettiLayer} pointerEvents="none">
            <LottieView
              source={require('../../../assets/lottie/confetti.json')}
              autoPlay
              loop
              resizeMode="cover"
              duration={CONFETTI_DURATION_MS}
              style={{ width, height }}
            />
          </View>
        )}

        {/* Centered card */}
        <View style={styles.centerWrap} pointerEvents="box-none">
          <View
            style={[
              styles.card,
              {
                backgroundColor: colors.surface,
                shadowColor: '#000',
                maxWidth: Math.min(420, width - s(32)),
              },
            ]}
            accessibilityRole={
              (Platform.OS === 'web' ? ('dialog' as any) : undefined)
            }
            accessibilityLabel={`${headline}. ${subline}`}>
            <Pressable
              onPress={closeX}
              accessibilityRole="button"
              accessibilityLabel="Close"
              style={styles.closeButton}
              hitSlop={12}>
              <Feather name="x" size={20} color={colors.textSecondary} />
            </Pressable>

            <View
              style={[
                styles.iconCircle,
                { backgroundColor: colors.successLight },
              ]}>
              <Feather
                name="check"
                size={28}
                color={colors.successDark}
              />
            </View>

            <Text
              style={[styles.headline, { color: colors.text }]}
              numberOfLines={2}>
              {headline}
            </Text>
            <Text
              style={[styles.subline, { color: colors.textSecondary }]}
              numberOfLines={2}>
              {subline}
            </Text>
            <Text
              style={[styles.micro, { color: colors.textSecondary }]}
              numberOfLines={1}>
              {microLine}
            </Text>

            <Pressable
              onPress={goPrimary}
              accessibilityRole="button"
              accessibilityLabel={primaryLabel}
              style={({ pressed }) => [
                styles.primaryBtn,
                { backgroundColor: colors.success},
                pressed && { opacity: 0.85 },
              ]}
              testID="wizard-home-celebration-primary">
              <Text
                style={[styles.primaryBtnText, { color: colors.textInverse }]}>
                {primaryLabel}
              </Text>
            </Pressable>

            <Pressable
              onPress={goSecondary}
              accessibilityRole="button"
              accessibilityLabel="Not Now"
              style={styles.secondaryBtn}
              testID="wizard-home-celebration-secondary">
              <Text
                style={[
                  styles.secondaryBtnText,
                  { color: colors.textSecondary },
                ]}>
                Not Now
              </Text>
            </Pressable>
          </View>
        </View>

        {/* Screen-reader-only status announcement (particles are decorative). */}
        <View
          accessible
          accessibilityLiveRegion="polite"
          accessibilityElementsHidden={false}
          style={styles.srOnly}>
          <Text>{`${headline} ${subline}`}</Text>
        </View>

        {/* Sink for full-screen height on web (Modal uses 100% height on
            native; on web this ensures the backdrop covers the viewport). */}
        <View style={{ height, width: 0 }} pointerEvents="none" />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  confettiLayer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
  },
  centerWrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    padding: s(16),
    zIndex: 2,
  },
  card: {
    width: '100%',
    borderRadius: 16,
    paddingHorizontal: s(20),
    paddingTop: v(28),
    paddingBottom: v(20),
    alignItems: 'center',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
  },
  closeButton: {
    position: 'absolute',
    top: v(10),
    right: s(10),
    padding: 6,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: v(14),
  },
  headline: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(22),
    lineHeight: fs(28),
    textAlign: 'center',
    marginBottom: v(6),
  },
  subline: {
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    lineHeight: fs(20),
    textAlign: 'center',
    marginBottom: v(4),
  },
  micro: {
    fontFamily: GTFonts.regular,
    fontSize: fs(13),
    lineHeight: fs(18),
    textAlign: 'center',
    marginBottom: v(20),
  },
  primaryBtn: {
    width: '100%',
    paddingVertical: v(14),
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: v(8),
  },
  primaryBtnText: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(15),
    lineHeight: fs(20),
  },
  secondaryBtn: {
    paddingVertical: v(10),
    paddingHorizontal: s(12),
  },
  secondaryBtnText: {
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    lineHeight: fs(18),
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    opacity: 0,
  },
});

export default WizardHomeCelebrationController;
